import { z } from "zod";

// Phase 11 typed configSchema for the `per-option-detail-card` template.
// Pure schema source — Plan 02 swaps it into TEMPLATE_CATALOG; this file ships no
// renderer. validateRecipe Pass 2A runs configSchema.safeParse(widget.config) so
// this schema is the config-shape gate a recipe's per-option-detail-card widget
// crosses.
//
// Shape rationale (closest analog: summary-checklist.ts for the item/section shape,
// branch-variant.ts for composition-by-id). A per-option-detail-card renders a
// composite detail card for each selected option of a composed option-set. The card
// is divided into ordered sections; each section references a recipe-level
// OptionCategory by id (composition-by-id, NOT nesting — the category data lives at
// the recipe level per the OptionCategory contract). perItemCheckboxes toggles a
// per-line checkbox affordance.
//
// Generality (the C1 grep gate): every field name and every comment here is generic —
// sections / categoryId / perItemCheckboxes / optionSetWidgetId. Recipe-declared ids
// carry any domain meaning; the engine never names a recipe's vocabulary.
//
// DoS bounds (T-11-01-01): sections.min(1).max(20); every string length-bounded.
// Bounds an unbounded extracted config before it reaches storage.
//
// Per-card checkmark state (which lines are ticked) is render-side and is
// intentionally NOT modeled here. Cross-widget existence checks (does
// optionSetWidgetId resolve? does each categoryId match a recipe OptionCategory?)
// are recipe-root-walk territory (future Pass 2B), not enforced at this layer.
//
// isolatedDeclarations + verbatimModuleSyntax + exactOptionalPropertyTypes (D-15):
// every exported type is an explicit interface and the exported const carries a
// `z.ZodType<PerOptionDetailCardConfig>` annotation so tsc emits declarations
// without reading across module boundaries (no TS9013).

export interface PerOptionDetailCardSection {
  id: string;
  label: string;
  categoryId: string;
}

export interface PerOptionDetailCardConfig {
  optionSetWidgetId: string;
  sections: ReadonlyArray<PerOptionDetailCardSection>;
  perItemCheckboxes: boolean;
}

// Per-section schema — kebab-case id + label + a categoryId referencing a
// recipe-level OptionCategory (by id; non-empty length-bounded string). The
// categoryId is a plain string ref here; the kebab-case shape is enforced on the
// section's own id, while categoryId stays a permissive non-empty ref to match the
// option-set._optionEntrySchema.categoryIds convention (recipe-level cross-checks
// happen in validateRecipe).
const _sectionSchema = z.strictObject({
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  label: z.string().min(1).max(200),
  categoryId: z.string().min(1).max(100),
});

// Top-level schema: optionSetWidgetId is the composition ref; sections.min(1): a
// detail card with zero sections is meaningless; sections.max(20): bounds card
// complexity. perItemCheckboxes is a required boolean toggle. The superRefine emits
// a per-index custom issue for duplicate section ids (one ctx.addIssue per offence).
export const PerOptionDetailCardConfigSchema: z.ZodType<PerOptionDetailCardConfig> = z
  .strictObject({
    optionSetWidgetId: z.string().min(1).max(100),
    sections: z.array(_sectionSchema).min(1).max(20),
    perItemCheckboxes: z.boolean(),
  })
  .superRefine((cfg, ctx) => {
    const seenIds = new Set<string>();
    for (const [i, section] of cfg.sections.entries()) {
      if (seenIds.has(section.id)) {
        ctx.addIssue({
          code: "custom",
          path: ["sections", i, "id"],
          message: `duplicate section id "${section.id}"`,
        });
      }
      seenIds.add(section.id);
    }
  });
